/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Fab, Zoom } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MavenDrawer from './MavenDrawer';
import MavenAvatar from './MavenAvatar';
import MAVEN from './mavenConfig';
import {
  AIADMIN_FONT_FAMILY,
  AIADMIN_PRIMARY_COLOR,
  AIADMIN_SURFACE_WHITE_HEX_COLOR,
  AIADMIN_TEXT_SECONDARY,
  AIADMIN_TRENDS_KPI_HOVER_SHADOW_COLOR
} from '../../common/styles/aiadminThemeTokens';

const INTRO_MS = 3000;
// One smooth, unhurried ease for folding and unfolding (shared by the intro and hover).
const FOLD = '340ms cubic-bezier(0.4, 0, 0.2, 1)';
// Ring around the button, so it reads as an AI assistant rather than a plain profile picture.
// Same orange-to-pink gradient as the dashboard cards' hover outline.
const RING = 'linear-gradient(135deg, #E49D2D 0%, #EA0B7E 51.09%, #851B4A 100%)';
const RING_PINK = '#EA0B7E';
// A pink wave that ripples out from the button every 3 seconds, until Maven is opened for the first time.
const ping = keyframes`
  0% { box-shadow: 0 0 0 0 ${RING_PINK}4D; }
  35%, 100% { box-shadow: 0 0 0 10px ${RING_PINK}00; }
`;

/**
 * Floating "Maven" button (bottom right). Clicking it turns the button into the chat window: the button
 * shrinks away and the window grows out of the same corner, taking its place. When the window closes, it
 * shrinks back into the corner and the button pops back in. Hackathon feature.
 */
const MavenFab = ({ pageContext, page, bottom, right }) => {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  // On page load the button shows in full ("M" + "Maven") so people notice it, then folds into the
  // round "B" after a few seconds, using the same smooth transition as hover.
  const [intro, setIntro] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIntro(false), INTRO_MS);
    return () => clearTimeout(timer);
  }, []);
  return (
    <>
      {/* Leaves at once when the window opens; comes back just after the window has shrunk away. */}
      <Zoom in={!open} timeout={{ enter: 220, exit: 120 }} style={{ transitionDelay: open ? '0ms' : '140ms' }}>
        <Fab
          variant="extended"
          size="medium"
          aria-label={`Chat with ${MAVEN.name}`}
          aria-expanded={open}
          onClick={() => {
            setOpen(true);
            setOpened(true);
          }}
          className={
            [intro && 'berlin-fab--intro', !opened && 'berlin-fab--pulse'].filter(Boolean).join(' ') || undefined
          }
          sx={{
            position: 'fixed',
            right,
            // Raised on pages that already have a button in this corner (Studio's Build With Berlin).
            bottom,
            zIndex: (theme) => theme.zIndex.modal + 1,
            // Only the avatar shows; the name slides out on hover or keyboard focus.
            // 4px padding + 40px avatar = a 48px circle, the same size as a standard Fab.
            width: 'auto',
            minWidth: 48,
            height: 48,
            p: '4px',
            borderRadius: '24px',
            // Dashboard navy, like the AI Admin cards and header.
            bgcolor: AIADMIN_PRIMARY_COLOR,
            color: AIADMIN_TEXT_SECONDARY,
            fontFamily: AIADMIN_FONT_FAMILY,
            fontWeight: 600,
            letterSpacing: 0.2,
            textTransform: 'none',
            boxShadow: `0 6px 20px ${AIADMIN_TRENDS_KPI_HOVER_SHADOW_COLOR}`,
            transition: `background-color 150ms ease, padding ${FOLD}`,
            // The ring: a 2px gradient border just outside the button, masked so only the edge shows.
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -2,
              p: '2px',
              // 2px rounder than the button (24px), so the ring stays an even circle or pill around it.
              borderRadius: '26px',
              background: RING,
              WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              WebkitMaskComposite: 'xor',
              mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              maskComposite: 'exclude',
              pointerEvents: 'none'
            },
            // The wave sits on its own layer, so it doesn't fight the button's shadow.
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: -2,
              borderRadius: '26px',
              pointerEvents: 'none'
            },
            '&.berlin-fab--pulse::after': { animation: `${ping} 3s ease-out 800ms infinite` },
            '& .berlin-fab-label': {
              maxWidth: 0,
              opacity: 0,
              ml: 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              transition: `max-width ${FOLD}, opacity 260ms ease, margin ${FOLD}`
            },
            '& .MuiAvatar-root': { transition: 'background-color 260ms ease, color 260ms ease' },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
              '&.berlin-fab--pulse::after': { animation: 'none' },
              '& .berlin-fab-label, & .MuiAvatar-root': { transition: 'none' }
            },
            '&:hover, &.Mui-focusVisible, &.berlin-fab--intro': {
              // Keep the same navy when expanded (Fab would otherwise apply its own hover colour).
              bgcolor: AIADMIN_PRIMARY_COLOR,
              pr: 2.25,
              // Room for the full name ("Berlin Intelligence").
              '& .berlin-fab-label': { maxWidth: 180, opacity: 1, ml: 1.25 },
              // Expanded: the "B" circle turns white, with a navy letter.
              '& .MuiAvatar-root': { bgcolor: AIADMIN_TEXT_SECONDARY, color: AIADMIN_PRIMARY_COLOR }
            }
          }}
        >
          {/* Navy avatar on the navy button: collapsed, it reads as one circle with a white "B". */}
          <Box sx={{ position: 'relative', display: 'flex' }}>
            <MavenAvatar size={40} />
            {/* Sparkle badge: the same AI mark Studio uses for its AI features. */}
            <Box
              component="span"
              aria-hidden
              sx={{
                position: 'absolute',
                // Above the pulse wave, which would otherwise wash over it.
                zIndex: 1,
                top: -7,
                right: -7,
                width: 18,
                height: 18,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: AIADMIN_SURFACE_WHITE_HEX_COLOR,
                color: RING_PINK,
                boxShadow: `0 1px 4px ${AIADMIN_TRENDS_KPI_HOVER_SHADOW_COLOR}`
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 12 }} />
            </Box>
          </Box>
          <span className="berlin-fab-label">{MAVEN.name}</span>
        </Fab>
      </Zoom>
      {/* Kept mounted while closed, so the answers from this session stay. */}
      <MavenDrawer
        open={open}
        onClose={() => setOpen(false)}
        pageContext={pageContext}
        page={page}
        bottom={bottom}
        right={right}
      />
    </>
  );
};

MavenFab.propTypes = {
  pageContext: PropTypes.shape({ page: PropTypes.string, graphId: PropTypes.string, version: PropTypes.number }),
  // AI Admin screen name (from the URL); used when there is no pageContext.
  page: PropTypes.string,
  /** Distance from the bottom of the screen, in px. */
  bottom: PropTypes.number,
  /** Distance from the right of the screen, in px. */
  right: PropTypes.number
};

MavenFab.defaultProps = { pageContext: null, page: null, bottom: 24, right: 24 };

export default MavenFab;
